import { withLocalStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { Injectable } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { DateTime } from 'luxon';

export type InfobarStateCollection = Record<string, number>;

export interface InfobarStateModel {
  infobars: InfobarStateCollection;
}

const initialState: InfobarStateModel = {
  infobars: {},
};

const EXPIRE_IN_MONTH: number = 1;

@Injectable({ providedIn: 'root' })
export class InfobarState extends signalStore(
  withState(initialState),

  withStorageSync(
    {
      key: 'cepInfobarData',
      select: (state: InfobarStateModel) => ({ ...state }),
    },
    withLocalStorage(),
  ),

  withMethods((store) => ({
    markClosed(uuid: string): void {
      if (!uuid) {
        return;
      }

      const now: DateTime = DateTime.now();
      const expiresOn: number = now.plus({ month: EXPIRE_IN_MONTH }).toMillis();

      const infobars: InfobarStateCollection = {
        ...store.infobars(),
        [uuid]: expiresOn,
      };

      patchState(store, { infobars });
    },

    isHidden(uuid: string): boolean {
      const infobars: InfobarStateCollection = store.infobars();
      const expiresOn: number | undefined = infobars[uuid];

      if (expiresOn === undefined) {
        return false;
      }

      const now: number = DateTime.now().toMillis();

      if (expiresOn < now) {
        const { [uuid]: removed, ...rest } = infobars;

        patchState(store, { infobars: rest });

        return false;
      }

      return true;
    },

    _cleanupExpired(): void {
      const now: number = DateTime.now().toMillis();
      const current: InfobarStateCollection = store.infobars();
      const active: InfobarStateCollection = {};

      for (const uuid in current) {
        const expiresOn: number = current[uuid];

        if (expiresOn > now) {
          active[uuid] = expiresOn;
        }
      }

      if (Object.keys(active).length !== Object.keys(current).length) {
        patchState(store, { infobars: active });
      }
    },
  })),

  withHooks((store) => ({
    onInit(): void {
      store._cleanupExpired();
    },
  })),
) {
}
